export function bindCountedSelect(wrapper) {
  const select = wrapper.querySelector('select')
  const overlay = wrapper.querySelector('[data-counted-select-overlay]')
  const trigger = wrapper.querySelector('[data-counted-select-trigger]')
  const selectedLabel = wrapper.querySelector('[data-counted-select-label]')
  const selectedCount = wrapper.querySelector('[data-counted-select-count]')
  const menu = wrapper.querySelector('[data-counted-select-menu]')
  if (!(select instanceof HTMLSelectElement)
    || !(trigger instanceof HTMLButtonElement)
    || ![overlay, selectedLabel, selectedCount, menu].every((element) => element instanceof HTMLElement)) {
    return () => {}
  }
  const labels = Array.from(select.labels || [])

  const close = () => {
    if (!menu.hidden) menu.hidden = true
    if (trigger.getAttribute('aria-expanded') !== 'false') trigger.setAttribute('aria-expanded', 'false')
  }

  const renderLabel = (element, option) => {
    element.textContent = option?.dataset.optionLabel || option?.textContent.trim() || ''
    const suffix = option?.dataset.optionSmallSuffix
    if (suffix && element.textContent.endsWith(suffix)) {
      element.textContent = element.textContent.slice(0, -suffix.length)
      const small = document.createElement('span')
      small.className = 'text-[length:calc(1em-2px)]'
      small.textContent = suffix
      element.appendChild(small)
    }
    if (option?.dataset.optionMutedLabel) {
      const muted = document.createElement('span')
      muted.className = 'text-gray-500'
      muted.textContent = option.dataset.optionMutedLabel
      element.appendChild(muted)
    }
  }

  const refresh = () => {
    const option = select.selectedOptions[0] || select.options[0]
    renderLabel(selectedLabel, option)
    selectedCount.textContent = option?.dataset.optionCount ? `${option.dataset.optionCount}件` : ''
    trigger.disabled = select.disabled
    if (select.disabled) close()
  }

  const selectOption = (option) => {
    select.value = option.value
    select.dispatchEvent(new Event('change', { bubbles: true }))
    close()
    trigger.focus()
  }

  const renderMenu = () => {
    const fragment = document.createDocumentFragment()
    const appendHeading = (label) => {
      const heading = document.createElement('span')
      heading.className = 'block px-3 py-1.5 text-xs font-bold text-gray-500'
      heading.textContent = label
      heading.setAttribute('role', 'presentation')
      fragment.appendChild(heading)
    }
    let previousOptionGroup = null
    Array.from(select.options).forEach((option) => {
      const optionGroup = option.parentElement instanceof HTMLOptGroupElement ? option.parentElement : null
      if (optionGroup !== previousOptionGroup) {
        previousOptionGroup = optionGroup
        if (optionGroup !== null) appendHeading(optionGroup.label)
      }
      if (option.disabled) {
        appendHeading(option.textContent.trim())
        return
      }

      const item = document.createElement('button')
      item.className = 'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-hapisumu-light-green-100 focus:bg-hapisumu-light-green-100 focus:outline-none'
      if (option.selected) {
        item.classList.add('bg-hapisumu-light-green-100', 'font-bold', 'text-hapisumu-dark-green')
      }
      item.type = 'button'
      item.setAttribute('role', 'option')
      item.setAttribute('aria-selected', String(option.selected))
      item.dataset.value = option.value

      const label = document.createElement('span')
      label.className = 'min-w-0 break-words'
      renderLabel(label, option)
      item.appendChild(label)

      if (option.dataset.optionCount) {
        const count = document.createElement('span')
        count.className = 'shrink-0 text-xs font-normal text-gray-500'
        count.textContent = `${option.dataset.optionCount}件`
        count.dataset.countedSelectOptionCount = ''
        item.appendChild(count)
      }

      item.addEventListener('click', () => selectOption(option))
      fragment.appendChild(item)
    })
    menu.replaceChildren(fragment)
  }

  const open = (focusSelected = false) => {
    if (trigger.disabled) return
    document.dispatchEvent(new CustomEvent('counted-select:open', { detail: wrapper }))
    renderMenu()
    menu.hidden = false
    trigger.setAttribute('aria-expanded', 'true')
    if (focusSelected) {
      setTimeout(() => {
        (menu.querySelector('[aria-selected="true"]') || menu.querySelector('[role="option"]'))?.focus()
      }, 0)
    }
  }

  trigger.addEventListener('click', () => {
    if (menu.hidden) {
      open()
    } else {
      close()
    }
  })
  labels.forEach((label) => {
    label.addEventListener('click', (event) => {
      event.preventDefault()
      trigger.focus()
      open()
    })
  })
  select.addEventListener('change', refresh)
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
      return
    }
    if (event.key !== 'ArrowDown') return
    event.preventDefault()
    open(true)
  })
  menu.addEventListener('keydown', (event) => {
    const items = Array.from(menu.querySelectorAll('[role="option"]'))
    const currentIndex = items.indexOf(document.activeElement)
    const targetIndex = event.key === 'ArrowDown'
      ? Math.min(currentIndex + 1, items.length - 1)
      : event.key === 'ArrowUp'
        ? Math.max(currentIndex - 1, 0)
        : null
    if (targetIndex !== null) {
      event.preventDefault()
      items[targetIndex]?.focus()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      close()
      trigger.focus()
    }
  })
  document.addEventListener('counted-select:open', (event) => {
    if (event.detail !== wrapper) close()
  })
  document.addEventListener('click', (event) => {
    if (menu.hidden) return
    const clickedLabel = labels.some((label) => label.contains(event.target))
    if (!wrapper.contains(event.target) && !clickedLabel) close()
  })
  wrapper.addEventListener('focusout', () => {
    if (menu.hidden) return
    setTimeout(() => {
      if (!wrapper.contains(document.activeElement)) close()
    }, 0)
  })

  refresh()
  select.classList.add('hidden')
  overlay.classList.remove('hidden')
  return refresh
}
